import type {
  LoginResponse,
  SarahRecapApi,
  UserChoiceValue,
  ApiOverrides,
} from '../types';

export type ApiMode = 'real' | 'mock';

export interface ApiClientConfig {
  /** Préfixe d'URL des endpoints Sarah (ex: "/api/sarah" ou "https://www.aaaep.fr/api/sarah"). */
  baseUrl: string;
  /** Si non précisé : auto-détection (mock en local, real sinon). */
  mode?: ApiMode;
  /**
   * Permet à l'hôte (Angular) d'intercepter chaque appel pour passer par son
   * HttpClient (interceptors, tokens). Si défini, l'override est utilisé en
   * lieu et place de l'appel `fetch` direct — y compris en mode mock.
   */
  overrides?: ApiOverrides;
}

/**
 * Client HTTP des endpoints Sarah (`do-check`, `user-datas`, `do-login`).
 *
 * En mode `mock`, simule des réponses cohérentes avec ce que renvoie la
 * version réelle (cf. `mockUserDatasResponse` dans la version vanille). Le
 * mode est figé à la construction.
 */
export class ApiClient {
  readonly mode: ApiMode;

  constructor(private readonly config: ApiClientConfig) {
    this.mode = config.mode ?? (this.isLocal() ? 'mock' : 'real');
  }

  /**
   * Vérifie si l'email existe déjà côté back. Un échec réseau ne doit pas
   * bloquer le parcours : on rapporte false et on warn (le bouton suivant
   * fonctionnera comme si l'email était neuf, le serveur tranchera à
   * l'inscription finale).
   */
  async checkMail(email: string): Promise<boolean> {
    if (this.config.overrides?.doCheck) {
      return this.config.overrides.doCheck(email);
    }
    if (this.mode === 'mock') {
      return /existe|known/i.test(email);
    }
    try {
      const res = await fetch(this.config.baseUrl + '/do-check', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ email }),
      });
      const json = (await res.json()) as { data?: unknown };
      return !!json.data;
    } catch (e) {
      console.warn('[sarah]', 'checkMail failed', e);
      return false;
    }
  }

  /**
   * Envoie la liste des choix utilisateur au back et récupère le récap
   * (situation, médecins, tests, démarches). En cas d'échec, retourne un
   * objet vide pour que le rendu du récap puisse continuer.
   */
  async saveUserData(payload: Array<[string, UserChoiceValue]>): Promise<SarahRecapApi> {
    if (this.config.overrides?.userDatas) {
      return this.config.overrides.userDatas(payload);
    }
    if (this.mode === 'mock') {
      return this.mockUserDatas(payload);
    }
    try {
      const res = await fetch(this.config.baseUrl + '/user-datas', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      const json = (await res.json()) as { data?: SarahRecapApi };
      return json.data ?? {};
    } catch (e) {
      console.warn('[sarah]', 'saveUserData failed', e);
      return {};
    }
  }

  /**
   * Authentifie sur l'API candidat. Le back renvoie `{ data: bool, message,
   * auth }`. On laisse l'erreur réseau remonter au composant (qui affiche un
   * message générique "erreur réseau") pour distinguer "mauvais mot de
   * passe" de "serveur injoignable".
   */
  async doLogin(email: string, password: string): Promise<LoginResponse> {
    if (this.config.overrides?.doLogin) {
      return this.config.overrides.doLogin(email, password);
    }
    if (this.mode === 'mock') {
      if (password === 'demo') {
        return {
          data: true,
          message: 'OK',
          auth: { accessToken: 'mock-token', userID: 1 },
        };
      }
      return {
        data: false,
        message: "L'email et/ou le mot de passe saisis sont incorrects.",
        auth: {},
      };
    }
    const res = await fetch(this.config.baseUrl + '/do-login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ formEmail: email, formPassword: password }),
    });
    return (await res.json()) as LoginResponse;
  }

  private isLocal(): boolean {
    if (typeof location === 'undefined') return false;
    return ['localhost', '127.0.0.1', ''].includes(location.hostname);
  }

  /**
   * Reproduit la forme attendue côté front (cf. `buildSarahHints` dans
   * SarahController côté Laravel). Construit une situation à partir des
   * clés visitées, et bascule entre médecins privés et préfecture selon
   * la présence de la clé `mr-autres` dans le parcours.
   */
  private mockUserDatas(payload: Array<[string, UserChoiceValue]>): SarahRecapApi {
    const valeurs = payload.map((p) =>
      typeof p[1] === 'string' ? p[1] : p[1] && p[1].key,
    );
    const hasMrAutres = valeurs.includes('mr-autres');
    return {
      situation: valeurs.filter((v): v is string => !!v),
      medecins_prefecture: !hasMrAutres,
      medecins: hasMrAutres
        ? [
            { nom: 'Dr Démo', adresse: '1 rue Exemple, 75000 Paris', contact: '01 23 45 67 89' },
            { nom: 'Dr Test', adresse: '2 rue Sample, 75000 Paris', contact: '01 23 45 67 90' },
          ]
        : [
            { libelle: 'Préfecture de Paris', lien: 'https://www.paris.fr' },
            { libelle: 'Préfecture du 92', lien: '#' },
          ],
      liste_tests: [
        {
          css: 'best-price',
          date_day_week: 'Lundi',
          date_mois_complet: '15 juin 2026',
          num_departement: '75',
          ville: 'Paris',
          adresse: '10 rue Démo',
          code_postal: '75001',
          council: 'Disponible',
          tarif_test: 90,
          oldTarif: 110,
          button: 'Réserver',
          button_left: '',
          id: 1,
        },
        {
          css: 'three-last',
          date_day_week: 'Mercredi',
          date_mois_complet: '17 juin 2026',
          num_departement: '75',
          ville: 'Paris',
          adresse: '20 avenue Test',
          code_postal: '75008',
          council: 'Bientôt complet',
          tarif_test: 95,
          oldTarif: null,
          button: 'Réserver',
          button_left: '3 places',
          id: 2,
        },
      ],
      demarches: [
        { demarche: 'Test psychotechnique', description: 'Démarches à partir de 3 mois.' },
        { demarche: 'Téléchargez votre CERFA', description: '' },
        {
          demarche: 'Visite médicale',
          description:
            'Selon votre situation, en commission médicale ou chez un médecin agréé.',
        },
        { demarche: "Démarches sur le site de l'ANTS", description: '' },
      ],
    };
  }
}
